import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next';
import { TextInput } from '@/components/common/text-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { formatToFullDateTime } from '@/utils/date-formatter';
import { TruncatedText } from '@/components/common/truncated-text';
import { SkeletonBasicInformation } from './skeleton-basic-information';
import { Switch } from '../ui/switch';
import { UserBasicInfoData } from '@/services/agent-v2-users-detail';

const Label = ({ label, required = true }: { label: string; required?: boolean }) => {
  return (
    <label className='flex w-48 mb-2.5'>
      <span className='text-sm font-medium text-[rgba(52,64,84,1)] mr-0.5'>{label}</span>
      <span className='text-[rgba(117,66,247,1)]'>{required ? '*' : ''}</span>
    </label>
  )
}

export interface BasicInformationProps {
  userBasicInfo: UserBasicInfoData;
  loading?: boolean;
  // nameError?: string;
  genderError?: string;
  clearUserDetail: () => void;
  changeValue: (name: keyof UserBasicInfoData, value: string | number) => void;
}

/** 用户信息基础表单 */
export const BasicInformation: FC<BasicInformationProps> = ({
  userBasicInfo,
  loading,
  // nameError,
  genderError,
  clearUserDetail,
  changeValue
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      // 组件卸载时清除用户详情
      clearUserDetail();
    }
  }, [location.pathname])

  const handleChange = (name: keyof UserBasicInfoData, value: string | number) => {
    changeValue(name, value);
  };

  return (
    <div className='relative pb-6 border-b border-[rgba(233,234,235,1)]'>
      <div className='text-lg font-semibold text-[rgba(16,24,40,1)] mb-6'>
        {t("agent_dashboard.user_detail.base_form.title")}
      </div>
      {loading && <SkeletonBasicInformation />}
      {!loading && <div className='w-full h-full overflow-hidden'>
        <div className='flex flex-wrap gap-5 mb-6'>
          <div className='w-80'>
            <Label label={t("agent_dashboard.user_detail.base_form.user_id")} required={false} />
            <div className='font-medium text-[rgba(16,24,40,1)] h-10 text-sm/[45px]'>
              <TruncatedText text={userBasicInfo.userGlobalId} />
            </div>
          </div>
          {/* <div className='w-80'>
            <Label label={t("agent_dashboard.user_detail.base_form.name")} />
            <TextInput
              type="text"
              value={userBasicInfo.name}
              className='h-10'
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!nameError}
            />
            <p className={cn(
              'text-xs text-[rgba(102,112,133,1)] pt-1.5',
              nameError && 'text-[rgba(217,45,32,1)]'
            )}>
              {t("agent_dashboard.user_detail.base_form.name_placeholder")}
            </p>
          </div> */}
          <div className='w-80'>
            <Label label={t("agent_dashboard.user_detail.base_form.email_address")} required={false} />
            <TruncatedText text={userBasicInfo.email} className='font-medium h-10 text-[rgba(16,24,40,1)] text-sm/[40px]' />
          </div>
          <div className='relative w-80'>
            <Label label={t("agent_dashboard.user_detail.base_form.gender")} />
            <div className='font-medium text-[rgba(16,24,40,1)] h-10 text-sm/10'>
              <RadioGroup
                value={userBasicInfo.gender}
                name="gender"
                className='flex items-center space-x-4'
                onValueChange={(value) => handleChange('gender', value)}
              >
                <label className='flex items-center cursor-pointer'>
                  <RadioGroupItem value="male" circleClassName='size-2' />
                  <span className='ml-2'>{t("agent_dashboard.user_detail.base_form.gender_male")}</span>
                </label>
                <label className='flex items-center cursor-pointer'>
                  <RadioGroupItem value="female" circleClassName='size-2' />
                  <span className='ml-2'>{t("agent_dashboard.user_detail.base_form.gender_woman")}</span>
                </label>
              </RadioGroup>
              {genderError && <p className='absolute left-1 bottom-2 text-xs text-[rgba(217,45,32,1)]'>{genderError}</p>}
            </div>
          </div>
          <div className='w-80 flex-shrink-0'>
            <Label label={t("agent_dashboard.user_detail.base_form.user_status")} />
            <Switch
              className='data-[state=checked]:bg-[rgba(18,183,106,1)]'
              checked={!!userBasicInfo.roleStatus}
              onCheckedChange={(checked) => handleChange('roleStatus', Number(checked))}
            />
          </div>
          <div className='w-80'>
            <Label label={t("agent_dashboard.user_detail.base_form.app_verify_status")} required={false} />
            <div className='font-medium text-[rgba(16,24,40,1)] h-10 text-sm/[45px]'>
              <span>
                {
                  userBasicInfo.appStatus === 1 ?
                    t("agent_dashboard.user_detail.base_form.verified") :
                    t("agent_dashboard.user_detail.base_form.unverified")
                }
              </span>
            </div>
          </div>
          <div className='w-80'>
            <Label label={t("agent_dashboard.user_detail.base_form.create_date")} required={false} />
            <div className='font-medium text-[rgba(16,24,40,1)] h-10 text-sm/[45px]'>
              <span>{formatToFullDateTime(userBasicInfo.createdAt)}</span>
            </div>
          </div>
          <div className='w-80'>
            <Label label={t("agent_dashboard.user_detail.base_form.deadline_for_use")} required={false} />
            <div className='font-medium text-[rgba(16,24,40,1)] h-10 text-sm/[45px]'>
              <span>{formatToFullDateTime(userBasicInfo.expireAt)}</span>
            </div>
          </div>
        </div>
      </div>}
    </div>
  )
}
